import React, { useEffect, useState, useCallback } from 'react';
import { useApp } from '../context/AppContext';
import { useAuth } from '../context/AuthContext';
import { FiSearch, FiMapPin, FiAlertTriangle, FiDroplet, FiThermometer, FiWind, FiActivity, FiRefreshCw, FiTrendingUp, FiTrendingDown, FiShield, FiInfo, FiSun, FiCloud, FiCloudRain } from 'react-icons/fi';
import { toast } from 'react-toastify';
import satelliteApi from '../services/satelliteApi';
import SatelliteMap from '../components/SatelliteMap';
import EventsTable from '../components/EventsTable';
import EventTimeline from '../components/EventTimeline';
import LossAdjusterPanel from '../components/LossAdjusterPanel';
import './Dashboard.css';

const Dashboard = () => {
  const { user } = useAuth();
  const { fetchEvents, fetchCommodities, selectedDisasterType, setSelectedDisasterType, events, stats, selectedEvent } = useApp();

  const [activeTab, setActiveTab] = useState('overview');
  const [locationQuery, setLocationQuery] = useState('');
  const [locationResults, setLocationResults] = useState([]);
  const [isSearching, setIsSearching] = useState(false);
  const [selectedLocation, setSelectedLocation] = useState(null);
  const [liveData, setLiveData] = useState(null);
  const [liveLoading, setLiveLoading] = useState(false);
  const [commodityData, setCommodityData] = useState(null);

  const defaultLat = selectedEvent?.location?.coordinates?.[1] || -26.2041;
  const defaultLng = selectedEvent?.location?.coordinates?.[0] || 28.0473;

  useEffect(() => {
    fetchEvents({ disasterType: selectedDisasterType });
    fetchCommodities();
  }, [selectedDisasterType]);

  // ─── Fetch consolidated satellite data ───
  const fetchLiveData = useCallback(async (lat, lng) => {
    setLiveLoading(true);
    try {
      const [fireResult, weatherResult, ratesResult, airResult] = await Promise.all([
        satelliteApi.getFiresNearby(lat, lng, 50, 7).catch(() => ({ fires: [], count: 0 })),
        satelliteApi.getEnhancedWeather(lat, lng).catch(() => null),
        satelliteApi.getCommodityRates(lat, lng).catch(() => null),
        satelliteApi.getAirQuality(lat, lng).catch(() => null),
      ]);

      const current = weatherResult?.weather?.current || weatherResult?.current || {};
      const forecast = weatherResult?.weather?.forecast || weatherResult?.forecast;

      setLiveData({
        fires: fireResult.fires || [],
        fireCount: fireResult.count || fireResult.fires?.length || 0,
        fireSource: fireResult.dataSource || 'NASA FIRMS',
        temp: current.temp || current.temperature || null,
        humidity: current.humidity || null,
        windSpeed: current.windSpeed || current.wind_speed || null,
        conditions: current.conditions || current.description || null,
        feelsLike: current.feelsLike || null,
        uvIndex: current.uvIndex || null,
        visibility: current.visibility || null,
        forecast: forecast?.daily?.slice(0, 5) || [],
        aqi: airResult?.aqi || airResult?.indexes?.[0]?.aqi || null,
        aqiCategory: airResult?.category || airResult?.indexes?.[0]?.category || null,
        weatherSource: weatherResult?.source || 'Google Weather',
        timestamp: new Date().toISOString(),
      });

      if (ratesResult?.success) {
        setCommodityData(ratesResult);
      }
    } catch (err) {
      console.error('Error fetching live data:', err);
    } finally {
      setLiveLoading(false);
    }
  }, []);

  useEffect(() => {
    const lat = selectedLocation?.lat || defaultLat;
    const lng = selectedLocation?.lng || defaultLng;
    fetchLiveData(lat, lng);
  }, [selectedLocation]);

  // ─── Location search ───
  const searchLocations = useCallback(async (query) => {
    if (!query || query.length < 2) { setLocationResults([]); return; }
    setIsSearching(true);
    try {
      const result = await satelliteApi.searchLocations(query);
      if (result.success && result.predictions) {
        setLocationResults(result.predictions);
      } else { setLocationResults([]); }
    } catch { setLocationResults([]); }
    finally { setIsSearching(false); }
  }, []);

  const handleSearchChange = (e) => {
    const val = e.target.value;
    setLocationQuery(val);
    clearTimeout(window._dashSearchTimer);
    window._dashSearchTimer = setTimeout(() => searchLocations(val), 350);
  };

  const handleLocationSelect = async (prediction) => {
    setLocationResults([]);
    setLocationQuery(prediction.description);
    try {
      const placeId = prediction.placeId || prediction.place_id;
      const details = await satelliteApi.getPlaceDetails(placeId);
      if (details.success && details.location) {
        setSelectedLocation({
          lat: details.location.lat,
          lng: details.location.lng,
          name: details.location.name || prediction.description,
          address: prediction.description
        });
        toast.success(`📍 Location set: ${details.location.name || prediction.description}`);
      }
    } catch {
      toast.error('Failed to get location details');
    }
  };

  // ─── Helper renderers ───
  const getWeatherIcon = () => {
    const c = (liveData?.conditions || '').toLowerCase();
    if (c.includes('rain') || c.includes('drizzle')) return <FiCloudRain />;
    if (c.includes('cloud') || c.includes('overcast')) return <FiCloud />;
    return <FiSun />;
  };

  const getAqiColor = (aqi) => {
    if (!aqi) return '#718096';
    if (aqi <= 50) return '#38a169';
    if (aqi <= 100) return '#d69e2e';
    if (aqi <= 150) return '#dd6b20';
    return '#e53e3e';
  };

  const getCommodityIcon = (name) => {
    const icons = { maize: '🌽', wheat: '🌾', cattle: '🐄', sheep: '🐑', sunflower: '🌻', soybeans: '🫘', tractors: '🚜', goats: '🐐' };
    return icons[name] || '📦';
  };

  const lat = selectedLocation?.lat || defaultLat;
  const lng = selectedLocation?.lng || defaultLng;
  const locationName = selectedLocation?.name || selectedEvent?.erfNumber || 'Johannesburg, South Africa';

  return (
    <div className="dashboard-v2">
      {/* ═══ HEADER ═══ */}
      <header className="dash-header">
        <div className="dash-header-left">
          <h1 className="dash-title">🛰️ Geoventory</h1>
          <span className="dash-subtitle">Earth Observation Dashboard</span>
        </div>
        <div className="dash-header-center">
          <nav className="dash-nav">
            {[
              { key: 'overview', icon: '📊', label: 'Overview' },
              { key: 'map', icon: '🗺️', label: 'Map' },
              { key: 'events', icon: '📋', label: 'Events' },
              { key: 'commodities', icon: '📈', label: 'Commodities' },
            ].map(tab => (
              <button
                key={tab.key}
                className={`dash-nav-btn ${activeTab === tab.key ? 'active' : ''}`}
                onClick={() => setActiveTab(tab.key)}
              >
                {tab.icon} {tab.label}
              </button>
            ))}
          </nav>
        </div>
        <div className="dash-header-right">
          <span className="user-badge">
            {user?.role === 'administrator' ? '👑' : user?.role === 'loss_adjuster' ? '🔍' : '👁️'}{' '}
            {user?.name?.split(' ')[0] || 'User'}
          </span>
        </div>
      </header>

      {/* ═══ LOCATION BAR ═══ */}
      <div className="dash-location-bar">
        <div className="dash-location-search">
          <FiSearch className="search-icon" />
          <input
            type="text"
            placeholder="Search any location for live satellite data..."
            value={locationQuery}
            onChange={handleSearchChange}
            className="location-input"
          />
          {isSearching && <span className="search-spin">⏳</span>}
          {locationResults.length > 0 && (
            <ul className="location-dropdown">
              {locationResults.map((r, i) => (
                <li key={r.placeId || r.place_id || i} onClick={() => handleLocationSelect(r)}>
                  <FiMapPin /> {r.description}
                </li>
              ))}
            </ul>
          )}
        </div>
        <div className="dash-location-info">
          <FiMapPin />
          <span className="loc-name">{locationName}</span>
          <span className="loc-coords">({lat.toFixed(4)}°, {lng.toFixed(4)}°)</span>
          <button className="refresh-btn" onClick={() => fetchLiveData(lat, lng)} disabled={liveLoading}>
            <FiRefreshCw className={liveLoading ? 'spinning' : ''} />
          </button>
        </div>
      </div>

      {/* ═══ DISASTER FILTER ═══ */}
      <div className="dash-disaster-bar">
        <span className="disaster-bar-label">⚠️ Disaster Type:</span>
        {[
          { id: 'all', icon: '🌍', label: 'All' },
          { id: 'wildfire', icon: '🔥', label: 'Wildfire' },
          { id: 'flood', icon: '🌊', label: 'Flood' },
          { id: 'drought', icon: '🌵', label: 'Drought' },
          { id: 'windstorm', icon: '💨', label: 'Windstorm' },
        ].map(dt => (
          <button
            key={dt.id}
            className={`disaster-pill ${selectedDisasterType === dt.id ? 'active' : ''}`}
            onClick={() => setSelectedDisasterType(dt.id)}
          >
            {dt.icon} {dt.label}
          </button>
        ))}
      </div>

      {/* ═══ MAIN CONTENT ═══ */}
      <main className="dash-main">
        {/* ═══════ OVERVIEW TAB ═══════ */}
        {activeTab === 'overview' && (
          <div className="dash-overview">
            {/* Live Satellite Conditions */}
            <section className="dash-section conditions-section">
              <h2 className="section-title"><FiActivity /> Live Satellite Conditions</h2>
              {liveLoading ? (
                <div className="dash-loading"><div className="spinner" /> Fetching live satellite data...</div>
              ) : liveData ? (
                <>
                  <div className="conditions-grid">
                    <div className="cond-card temp-card">
                      <div className="cond-icon"><FiThermometer /></div>
                      <div className="cond-data">
                        <span className="cond-value">{liveData.temp != null ? `${Math.round(liveData.temp)}°C` : '—'}</span>
                        <span className="cond-label">Temperature</span>
                        {liveData.feelsLike != null && <span className="cond-sub">Feels like {Math.round(liveData.feelsLike)}°C</span>}
                      </div>
                    </div>
                    <div className="cond-card humid-card">
                      <div className="cond-icon"><FiDroplet /></div>
                      <div className="cond-data">
                        <span className="cond-value">{liveData.humidity != null ? `${liveData.humidity}%` : '—'}</span>
                        <span className="cond-label">Humidity</span>
                      </div>
                    </div>
                    <div className="cond-card wind-card">
                      <div className="cond-icon"><FiWind /></div>
                      <div className="cond-data">
                        <span className="cond-value">{liveData.windSpeed != null ? `${liveData.windSpeed} km/h` : '—'}</span>
                        <span className="cond-label">Wind Speed</span>
                      </div>
                    </div>
                    <div className="cond-card fire-card">
                      <div className="cond-icon fire-icon"><FiAlertTriangle /></div>
                      <div className="cond-data">
                        <span className="cond-value">{liveData.fireCount}</span>
                        <span className="cond-label">Fires (100km)</span>
                        <span className="cond-sub">{liveData.fireSource}</span>
                      </div>
                    </div>
                    <div className="cond-card weather-card">
                      <div className="cond-icon">{getWeatherIcon()}</div>
                      <div className="cond-data">
                        <span className="cond-value">{liveData.conditions || '—'}</span>
                        <span className="cond-label">Conditions</span>
                      </div>
                    </div>
                    <div className="cond-card aqi-card">
                      <div className="cond-icon" style={{ color: getAqiColor(liveData.aqi) }}><FiShield /></div>
                      <div className="cond-data">
                        <span className="cond-value" style={{ color: getAqiColor(liveData.aqi) }}>{liveData.aqi || '—'}</span>
                        <span className="cond-label">Air Quality</span>
                        {liveData.aqiCategory && <span className="cond-sub">{liveData.aqiCategory}</span>}
                      </div>
                    </div>
                  </div>
                  <div className="conditions-footer">
                    <span className="live-dot" />
                    <small>Sources: {liveData.fireSource} • {liveData.weatherSource} • Updated {new Date(liveData.timestamp).toLocaleTimeString()}</small>
                  </div>
                </>
              ) : (
                <div className="dash-empty-msg">No satellite data available. Try refreshing or searching a location.</div>
              )}
            </section>

            {/* Map + Events summary side by side */}
            <div className="dash-two-col">
              <section className="dash-section map-overview-section">
                <h2 className="section-title">🗺️ Satellite Map</h2>
                <div className="map-container-dash">
                  <SatelliteMap />
                </div>
              </section>

              <section className="dash-section events-summary-section">
                <h2 className="section-title"><FiAlertTriangle /> Active Events</h2>
                <div className="events-stats-row">
                  <div className="ev-stat">
                    <span className="ev-stat-val">{events?.length || 0}</span>
                    <span className="ev-stat-lbl">Total Events</span>
                  </div>
                  <div className="ev-stat">
                    <span className="ev-stat-val">{Math.round(stats?.avgLossPercentage || 0)}%</span>
                    <span className="ev-stat-lbl">Avg Loss</span>
                  </div>
                  <div className="ev-stat danger">
                    <span className="ev-stat-val">R {((stats?.totalValueLoss || 0) / 1000000).toFixed(1)}M</span>
                    <span className="ev-stat-lbl">Total Loss</span>
                  </div>
                  <div className="ev-stat">
                    <span className="ev-stat-val">{stats?.totalClaims || 0}</span>
                    <span className="ev-stat-lbl">Claims</span>
                  </div>
                </div>
                <div className="timeline-container-dash">
                  <EventTimeline />
                </div>
              </section>
            </div>

            {/* Commodity overview */}
            {commodityData && (
              <section className="dash-section commodity-overview-section">
                <h2 className="section-title"><FiTrendingUp /> Satellite-Adjusted Commodity Rates</h2>
                {commodityData.landUse && (
                  <div className="land-use-bar">
                    <span className="lu-icon">
                      {commodityData.landUse.type === 'URBAN' ? '🏙️' :
                       commodityData.landUse.type === 'SUBURBAN' ? '🏘️' :
                       commodityData.landUse.type === 'AGRICULTURAL' ? '🌾' :
                       commodityData.landUse.type === 'PASTORAL' ? '🐄' : '📍'}
                    </span>
                    <span className="lu-type">{(commodityData.landUse.type || 'UNKNOWN').replace(/_/g, '-')}</span>
                    <span className="lu-desc">{commodityData.landUse.description}</span>
                    {commodityData.landUse.confidence > 0 && (
                      <span className="lu-conf">{commodityData.landUse.confidence}% confidence</span>
                    )}
                  </div>
                )}
                {commodityData.relevance && (
                  <div className="relevance-bar">
                    <FiInfo size={14} />
                    <span>{commodityData.relevance.reason}</span>
                  </div>
                )}
                {commodityData.rates && Object.keys(commodityData.rates).length > 0 && (
                  <div className="commodity-grid-dash">
                    {Object.entries(commodityData.rates).map(([name, data]) => (
                      <div key={name} className="comm-card">
                        <div className="comm-header">
                          <span className="comm-icon">{getCommodityIcon(name)}</span>
                          <span className="comm-name">{name.charAt(0).toUpperCase() + name.slice(1)}</span>
                          {data.isLivePrice && <span className="comm-live">LIVE</span>}
                        </div>
                        <div className="comm-price">R {data.adjustedRate?.toLocaleString()}<small>/{data.unit}</small></div>
                        {data.multiplier && data.multiplier !== 1 && (
                          <div className={`comm-adj ${data.multiplier > 1 ? 'up' : 'down'}`}>
                            {data.multiplier > 1 ? <FiTrendingUp /> : <FiTrendingDown />}
                            {Math.abs((data.multiplier - 1) * 100).toFixed(0)}%
                          </div>
                        )}
                        <div className="comm-source"><small>{data.priceSource || 'Market data'}</small></div>
                      </div>
                    ))}
                  </div>
                )}
                {commodityData.referenceRates && Object.keys(commodityData.referenceRates).length > 0 && (
                  <>
                    <h3 className="ref-title">📋 Regional SAFEX Rates (Reference Only)</h3>
                    <div className="commodity-grid-dash ref-grid">
                      {Object.entries(commodityData.referenceRates).map(([name, data]) => (
                        <div key={name} className="comm-card ref-card">
                          <div className="comm-header">
                            <span className="comm-icon">{getCommodityIcon(name)}</span>
                            <span className="comm-name">{name.charAt(0).toUpperCase() + name.slice(1)}</span>
                            <span className="comm-ref-badge">REF</span>
                          </div>
                          <div className="comm-price">R {data.baseRate?.toLocaleString()}<small>/{data.unit}</small></div>
                          <div className="comm-source"><small>{data.priceSource || 'SAFEX'}</small></div>
                        </div>
                      ))}
                    </div>
                  </>
                )}
              </section>
            )}
          </div>
        )}

        {/* ═══════ MAP TAB ═══════ */}
        {activeTab === 'map' && (
          <div className="dash-fullscreen-map">
            <SatelliteMap fullscreen />
          </div>
        )}

        {/* ═══════ EVENTS TAB ═══════ */}
        {activeTab === 'events' && (
          <div className="dash-events-tab">
            <EventsTable expanded />
            {(user?.role === 'administrator' || user?.role === 'loss_adjuster') && (
              <LossAdjusterPanel />
            )}
          </div>
        )}

        {/* ═══════ COMMODITIES TAB ═══════ */}
        {activeTab === 'commodities' && (
          <div className="dash-commodities-tab">
            <section className="dash-section">
              <h2 className="section-title"><FiTrendingUp /> Satellite-Adjusted Commodity Analysis</h2>
              <p className="section-desc">
                Live commodity prices sourced from SAFEX/JSE and World Bank, adjusted using NASA FIRMS fire data,
                Google Weather conditions, and land use classification at the selected location.
              </p>

              {liveData && (
                <div className="comm-conditions-bar">
                  <span>🌡️ {liveData.temp != null ? `${Math.round(liveData.temp)}°C` : '—'}</span>
                  <span>💧 {liveData.humidity != null ? `${liveData.humidity}%` : '—'}</span>
                  <span>🔥 {liveData.fireCount} fires nearby</span>
                  <span>📍 {locationName}</span>
                </div>
              )}

              {commodityData?.landUse && (
                <div className="comm-land-use-detail">
                  <div className="lu-detail-header">
                    <span className="lu-detail-icon">
                      {commodityData.landUse.type === 'URBAN' ? '🏙️' :
                       commodityData.landUse.type === 'SUBURBAN' ? '🏘️' :
                       commodityData.landUse.type === 'AGRICULTURAL' ? '🌾' :
                       commodityData.landUse.type === 'PASTORAL' ? '🐄' :
                       commodityData.landUse.type === 'PERI_URBAN' ? '🏡' : '📍'}
                    </span>
                    <div>
                      <strong>{(commodityData.landUse.type || 'UNKNOWN').replace(/_/g, '-')} ZONE</strong>
                      <p>{commodityData.landUse.description}</p>
                    </div>
                    {commodityData.landUse.confidence > 0 && (
                      <div className="lu-conf-badge">{commodityData.landUse.confidence}%</div>
                    )}
                  </div>
                  <div className="lu-indicators">
                    {commodityData.landUse.vegetationIndex != null && (
                      <div className="lu-ind">
                        <span>🌿 Vegetation</span>
                        <strong>{(commodityData.landUse.vegetationIndex * 100).toFixed(0)}%</strong>
                      </div>
                    )}
                    {commodityData.landUse.soilMoisture != null && (
                      <div className="lu-ind">
                        <span>💧 Soil Moisture</span>
                        <strong>{(commodityData.landUse.soilMoisture * 100).toFixed(0)}%</strong>
                      </div>
                    )}
                    {commodityData.landUse.agriculturalRadius > 0 && (
                      <div className="lu-ind">
                        <span>🚜 Nearest Farm</span>
                        <strong>{commodityData.landUse.agriculturalRadius} km</strong>
                      </div>
                    )}
                  </div>
                </div>
              )}

              {commodityData?.relevance && (
                <div className={`comm-relevance-notice ${commodityData.rates && Object.keys(commodityData.rates).length > 0 ? 'has-data' : 'no-data'}`}>
                  <span>{commodityData.rates && Object.keys(commodityData.rates).length > 0 ? '✅' : 'ℹ️'}</span>
                  <div>
                    <strong>{commodityData.relevance.reason}</strong>
                    {commodityData.relevance.suggestion && <p>{commodityData.relevance.suggestion}</p>}
                  </div>
                </div>
              )}

              {commodityData?.rates && Object.keys(commodityData.rates).length > 0 && (
                <>
                  <h3 className="comm-section-heading">📊 Commodities at This Location</h3>
                  <div className="commodity-grid-dash large-grid">
                    {Object.entries(commodityData.rates).map(([name, data]) => (
                      <div key={name} className="comm-card-lg">
                        <div className="comm-lg-top">
                          <span className="comm-lg-icon">{getCommodityIcon(name)}</span>
                          <div>
                            <div className="comm-lg-name">{name.charAt(0).toUpperCase() + name.slice(1)}</div>
                            <div className="comm-lg-cat">{data.category}</div>
                          </div>
                          {data.isLivePrice && <span className="comm-live">🟢 LIVE</span>}
                        </div>
                        <div className="comm-lg-price">R {data.adjustedRate?.toLocaleString()}<small> /{data.unit}</small></div>
                        <div className="comm-lg-base">Base: R {data.baseRate?.toLocaleString()}</div>
                        {data.multiplier !== 1 && (
                          <div className={`comm-lg-adj ${data.multiplier > 1 ? 'up' : 'down'}`}>
                            {data.multiplier > 1 ? '▲' : '▼'} {Math.abs((data.multiplier - 1) * 100).toFixed(0)}% adjustment
                          </div>
                        )}
                        {data.factors?.length > 0 && (
                          <div className="comm-lg-factors">
                            {data.factors.map((f, i) => <small key={i}>{f}</small>)}
                          </div>
                        )}
                        <div className="comm-lg-source">{data.priceSource || 'Market data'}</div>
                      </div>
                    ))}
                  </div>
                </>
              )}

              {commodityData?.referenceRates && Object.keys(commodityData.referenceRates).length > 0 && (
                <>
                  <h3 className="comm-section-heading ref-heading">📋 Regional SAFEX Market Rates (Reference Only)</h3>
                  <p className="comm-ref-note">These are regional market rates for context. They do not represent commodities at this location.</p>
                  <div className="commodity-grid-dash large-grid ref-grid">
                    {Object.entries(commodityData.referenceRates).map(([name, data]) => (
                      <div key={name} className="comm-card-lg ref-card-lg">
                        <div className="comm-lg-top">
                          <span className="comm-lg-icon">{getCommodityIcon(name)}</span>
                          <div>
                            <div className="comm-lg-name">{name.charAt(0).toUpperCase() + name.slice(1)}</div>
                            <div className="comm-lg-cat">{data.category}</div>
                          </div>
                          <span className="comm-ref-badge-lg">REF</span>
                        </div>
                        <div className="comm-lg-price ref-price">R {data.baseRate?.toLocaleString()}<small> /{data.unit}</small></div>
                        <div className="comm-lg-source">{data.priceSource || 'SAFEX'}</div>
                      </div>
                    ))}
                  </div>
                </>
              )}

              {(!commodityData?.rates || Object.keys(commodityData.rates).length === 0) &&
               (!commodityData?.referenceRates || Object.keys(commodityData.referenceRates).length === 0) && (
                <div className="comm-empty">
                  <span>🏙️</span>
                  <h4>No Commodity Data</h4>
                  <p>Select a location to load satellite-adjusted commodity rates.</p>
                </div>
              )}

              <div className="comm-disclaimer">
                <FiInfo size={14} />
                <small>
                  Land use classified via Google Geocoding + Open-Meteo vegetation data.
                  Prices from SAFEX/JSE contracts and World Bank. Adjustments from NASA FIRMS fire data and live weather.
                </small>
              </div>
            </section>
          </div>
        )}
      </main>
    </div>
  );
};

export default Dashboard;
